import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useLanguage, useDynamicTranslation, PriceTag } from '../context/LanguageContext';
import API from '../config/api';
import IMAGES from '../config/images';
import ReactMarkdown from 'react-markdown';
import freeDeliveryIcon from '../assets/free-delivery-free-svgrepo-com (1).svg';
import handcraftedIcon from '../assets/craft-wood-chisel-svgrepo-com.svg';
import returnsIcon from '../assets/return-svgrepo-com.svg';
import checkoutIcon from '../assets/cart-checkout-ecommerce-svgrepo-com.svg';

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { lang, t, formatPrice } = useLanguage();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [selectedSizeObj, setSelectedSizeObj] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [activeAccordion, setActiveAccordion] = useState('details');
  const [selectedThumb, setSelectedThumb] = useState(0);
  const [wishlisted, setWishlisted] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewAvg, setReviewAvg] = useState(0);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [canReview, setCanReview] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, text: '', images: [] });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    setLoading(true);
    setShowFullDescription(false);
    fetch(API.products.get(id))
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') setProduct(data.data);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [id]);

  const p = product || {};
  const imgSrc = p.image_url || IMAGES.products[((parseInt(id) || 1) - 1) % IMAGES.products.length];

  // Parse sizes (Legacy comma-separated or new JSON array)
  let parsedSizes = [];
  try {
    if (p.size_variants && p.size_variants.startsWith('[')) parsedSizes = JSON.parse(p.size_variants.replace(/&amp;/g, '&'));
    else if (p.sizes && p.sizes.length > 0) parsedSizes = p.sizes.split(',').map(s => ({ size: s.trim().replace(/&amp;/g, '&') }));
  } catch (e) {
    parsedSizes = [];
  }

  // Parse materials
  const parsedMaterials = p.materials ? p.materials.split(',').map(s => s.trim().replace(/&amp;/g, '&')).filter(Boolean) : [];

  useEffect(() => {
    if (parsedSizes.length > 0) {
      const existing = selectedSizeObj ? parsedSizes.find(s => s.size === selectedSizeObj.size) : null;
      if (!existing) setSelectedSizeObj(parsedSizes[0]);
    }
  }, [p.size_variants, p.sizes]);

  useEffect(() => {
    if (parsedMaterials.length > 0 && !selectedMaterial) {
      setSelectedMaterial(parsedMaterials[0]);
    }
  }, [p.materials]);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} - Kaaleen Haus`;

      const setMeta = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('property', property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      setMeta('og:title', `${product.name} - Kaaleen Haus`);
      setMeta('og:description', product.description || 'Discover luxury handcrafted rugs at Kaaleen Haus.');
      if (product.image_url) setMeta('og:image', product.image_url);
      setMeta('og:url', window.location.href);
      setMeta('og:type', 'product');

      // SEO keywords meta tag
      if (product.seo_keywords) {
        let metaKw = document.querySelector('meta[name="keywords"]');
        if (!metaKw) {
          metaKw = document.createElement('meta');
          metaKw.setAttribute('name', 'keywords');
          document.head.appendChild(metaKw);
        }
        metaKw.setAttribute('content', product.seo_keywords);
      }

      // Fetch reviews
      fetch(API.reviews.list(product.id))
        .then(r => r.json())
        .then(d => { if (d.status === 'success') { setReviews(d.data || []); setReviewAvg(d.average || 0); setReviewTotal(d.total || 0); } })
        .catch(() => {});

      // Check if user can review
      if (user?.token) {
        fetch(API.orders.userOrders, { headers: { 'Authorization': `Bearer ${user.token}` } })
          .then(r => r.json())
          .then(d => {
            if (d.status === 'success') {
              const delivered = (d.data || []).filter(o => o.status === 'delivered');
              const hasProduct = delivered.some(o => (o.items || []).some(i => String(i.product_id) === String(product.id)));
              setCanReview(hasProduct);
            }
          })
          .catch(() => {});
      }

      // Fetch related products
      fetch(API.products.list)
        .then(r => r.json())
        .then(d => {
          if (d.status === 'success') {
            const cur = product;
            const related = (d.data || []).filter(pp => pp.id !== cur.id && (
              pp.category_id === cur.category_id ||
              (pp.colors && cur.colors && pp.colors.split(',').some(c => cur.colors.includes(c.trim()))) ||
              (pp.shapes && cur.shapes && pp.shapes.split(',').some(s => cur.shapes.includes(s.trim()))) ||
              (pp.constructions && cur.constructions && pp.constructions === cur.constructions)
            )).slice(0, 8);
            setRelatedProducts(related);
          }
        })
        .catch(() => {});
    }
  }, [product]);

  const activeB2C = (selectedSizeObj && selectedSizeObj.b2c) ? selectedSizeObj.b2c : p.b2c_price;
  const discountPercentRaw = Number(p.category_discount_percent || 0);
  const discountPercent = Number.isFinite(discountPercentRaw) ? Math.max(0, Math.min(95, discountPercentRaw)) : 0;
  const discountedB2C = discountPercent > 0
    ? Math.round((Number(activeB2C || 0) * (1 - (discountPercent / 100))) * 100) / 100
    : Number(activeB2C || 0);
  const price = discountedB2C;

  // Decode HTML entities in description (fixes &#039; → ' etc.)
  const decodeEntities = (str) => {
    if (!str) return '';
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  };
  const descriptionText = decodeEntities(p.description || '').trim();
  const canCollapseDescription = descriptionText.length > 260;
  const deliveryType = (p.delivery_type || '').toLowerCase() === 'made_to_order' ? 'made_to_order' : 'stock';
  const fallbackRange = deliveryType === 'made_to_order' ? [15, 20] : [1, 3];
  const dispatchMinDays = Math.max(0, Number.isFinite(Number(p.dispatch_min_days)) ? Number(p.dispatch_min_days) : fallbackRange[0]);
  const dispatchMaxDays = Math.max(dispatchMinDays, Number.isFinite(Number(p.dispatch_max_days)) ? Number(p.dispatch_max_days) : fallbackRange[1]);
  const dispatchWindowText = dispatchMinDays === dispatchMaxDays
    ? (lang === 'de' ? `${dispatchMinDays} Tag Versand` : `${dispatchMinDays} day dispatch`)
    : (lang === 'de' ? `${dispatchMinDays}-${dispatchMaxDays} Tage Versand` : `${dispatchMinDays}-${dispatchMaxDays} day dispatch`);
  const deliveryTypeLabel = deliveryType === 'made_to_order' ? (lang === 'de' ? 'Auf Bestellung' : 'Made to order') : (lang === 'de' ? 'Auf Lager' : 'In stock');
  const hasTrackedStock = deliveryType === 'stock' && p.stock_quantity !== null && p.stock_quantity !== undefined && p.stock_quantity !== '';
  const stockQuantity = hasTrackedStock ? Math.max(0, Number(p.stock_quantity)) : null;
  const outOfStock = hasTrackedStock && stockQuantity === 0;
  const lowStock = hasTrackedStock && stockQuantity > 0 && stockQuantity <= 5;
  const maxSelectableQty = hasTrackedStock ? Math.max(1, stockQuantity) : 999;

  useEffect(() => {
    if (hasTrackedStock && quantity > maxSelectableQty) {
      setQuantity(maxSelectableQty);
    }
  }, [hasTrackedStock, quantity, maxSelectableQty]);

  // Parse additional images if they exist
  let additional = [];
  try {
    if (p.additional_images) {
      additional = JSON.parse(p.additional_images);
    }
  } catch (e) {
    console.error("Failed to parse additional images", e);
  }

  // Gallery: Main Image + Video + Additional Images
  let galleryItems = [];
  if (p.image_url) {
    galleryItems.push({ type: 'image', url: p.image_url });
    if (p.video_url) {
      galleryItems.push({ type: 'video', url: p.video_url });
    }
    additional.forEach(url => galleryItems.push({ type: 'image', url }));
  } else {
    galleryItems.push({ type: 'image', url: IMAGES.products[0] });
  }

  const handleAddToCart = () => {
    if (outOfStock) return;

    const safeQuantity = Math.max(1, Math.min(quantity, maxSelectableQty));
    addToCart({
      ...p,
      price,
      original_price: Number(activeB2C || 0),
      category_discount_percent: discountPercent,
      stock_quantity: hasTrackedStock ? stockQuantity : null,
      material: selectedMaterial || p.materials,
      size: selectedSizeObj?.size || 'Standard'
    }, safeQuantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  const toggleAccordion = (key) => {
    setActiveAccordion(activeAccordion === key ? null : key);
  };

  // Translation Hooks
  const translatedName = useDynamicTranslation(p?.name);
  const translatedCategory = useDynamicTranslation(p?.category_name);

  // Loading
  if (loading) {
    return (
      <div className="pd-loading">
        <div className="pd-loader"></div>
        <p>{lang === 'de' ? t('common.loading') : 'Loading product...'}</p>
        <style>{pdStyles}</style>
      </div>
    );
  }

  // Not found
  if (!product) {
    return (
      <div className="pd-loading">
        <p style={{ fontSize: '1.2rem', marginBottom: '10px' }}>{lang === 'de' ? t('common.notFound') : 'Product not found'}</p>
        <Link to="/shop" style={{ color: '#785600', textDecoration: 'underline' }}>← {lang === 'de' ? t('product.shop') : 'Back to Shop'}</Link>
        <style>{pdStyles}</style>
      </div>
    );
  }

  return (
    <div className="pd-wrapper">
      <div className="pd-page">
        {/* ─── Breadcrumb ─── */}
        <div className="pd-breadcrumb" style={{ position: 'relative' }}>
          <Link to="/">{lang === 'de' ? t('product.home') : 'Home'}</Link>
          <span>/</span>
          <Link to="/shop">{lang === 'de' ? t('product.shop') : 'Rugs'}</Link>
          {p.category_name && <><span>/</span><Link to={`/shop?category=${p.category_id}`}>{translatedCategory || p.category_name}</Link></>}
          <span>/</span>
          <span className="bc-current">{translatedName || p.name}</span>
          <button
            className="pd-share-btn"
            onClick={(e) => {
              e.preventDefault();
              if (navigator.share) {
                navigator.share({ title: p.name, text: `Check out this product: ${p.name}`, url: window.location.href }).catch(() => { });
              } else {
                window.open(`https://wa.me/?text=Check%20out%20this%20product:%20${encodeURIComponent(p.name)}%20-%20${encodeURIComponent(window.location.href)}`, '_blank');
              }
            }}
            title="Share"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
          </button>
        </div>

        {/* ─── Main Layout ─── */}
        <div className="pd-layout">

          {/* ═══ LEFT: Image Gallery ═══ */}
          <div className="pd-gallery">
            <div className="pd-thumbs">
              {galleryItems.map((item, i) => (
                <div
                  key={i}
                  className={`pd-thumb ${selectedThumb === i ? 'active' : ''}`}
                  onClick={() => setSelectedThumb(i)}
                  style={item.type === 'image' ? { backgroundImage: `url("${item.url}")` } : { background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {item.type === 'video' && <span style={{ fontSize: '20px', color: '#fff', opacity: 0.8 }}>▶</span>}
                </div>
              ))}
            </div>
            <div className="pd-hero-img">
              {galleryItems[selectedThumb]?.type === 'image' ? (
                <div className="pd-hero-inner" style={{ backgroundImage: `url("${galleryItems[selectedThumb].url}")` }}></div>
              ) : (
                <div className="pd-hero-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', inset: 0 }}>
                  <video src={galleryItems[selectedThumb]?.url} controls autoPlay loop playsInline style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', outline: 'none' }} />
                </div>
              )}
              {galleryItems.length > 1 && (
                <>
                  <button
                    className="pd-nav-btn prev"
                    onClick={(e) => { e.stopPropagation(); setSelectedThumb((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1)); }}
                  >
                    ‹
                  </button>
                  <button
                    className="pd-nav-btn next"
                    onClick={(e) => { e.stopPropagation(); setSelectedThumb((prev) => (prev === galleryItems.length - 1 ? 0 : prev + 1)); }}
                  >
                    ›
                  </button>
                </>
              )}
              {galleryItems[selectedThumb]?.type === 'image' && (
                <button className="pd-zoom-btn">⊕</button>
              )}
            </div>
          </div>

          {/* ═══ RIGHT: Product Info ═══ */}
          <div className="pd-info">
            {/* Label */}
            <span className="pd-label">
              {p.category_name ? (translatedCategory || p.category_name).toUpperCase() : (lang === 'de' ? t('common.handcrafted').toUpperCase() : 'HANDCRAFTED')}
            </span>

            {/* Title */}
            <h1 className="pd-title">{translatedName || p.name}</h1>

            {/* Rating + SKU */}
            <div className="pd-meta-row">
              <div className="pd-rating">
                {[1,2,3,4,5].map(s => (
                  <span key={s} className="pd-star" style={{ color: s <= Math.round(reviewAvg) ? '#f5a623' : '#d3c4af' }}>★</span>
                ))}
                <span className="pd-rating-text">{reviewAvg} ({reviewTotal} {lang === 'de' ? (reviewTotal !== 1 ? 'Bewertungen' : 'Bewertung') : (reviewTotal !== 1 ? 'reviews' : 'review')})</span>
              </div>
              {p.sku && <span className="pd-sku">{lang === 'de' ? t('product.sku') : 'SKU'}: {p.sku}</span>}
              <span className="pd-delivery-pill">{deliveryTypeLabel} · {dispatchWindowText}</span>
            </div>

            {/* Price */}
            <div className="pd-price-row">
              <span className="pd-price">{formatPrice(Number(price || activeB2C)).display}</span>
              {discountPercent > 0 && (
                <span className="pd-mrp" style={{ textDecoration: 'line-through', marginLeft: '10px', color: '#999', fontSize: '0.85em' }}>{formatPrice(Number(activeB2C || 0)).display}</span>
              )}
              {discountPercent > 0 && (
                <span className="pd-discount-badge">{discountPercent}% {lang === 'de' ? 'RABATT' : 'OFF'}</span>
              )}
            </div>

            {outOfStock && <div className="pd-stock-note out">{lang === 'de' ? 'Nicht auf Lager' : 'Out of stock'}</div>}
            {!outOfStock && lowStock && <div className="pd-stock-note low">{lang === 'de' ? t('product.stockLeft')(stockQuantity) : `Only ${stockQuantity} left in stock`}</div>}

            {/* Description */}
            <div className={`pd-description ${canCollapseDescription && !showFullDescription ? 'collapsed' : ''}`}>
              <ReactMarkdown>{descriptionText}</ReactMarkdown>
              {canCollapseDescription && !showFullDescription && <div className="pd-description-fade" />}
            </div>
            {canCollapseDescription && (
              <button
                type="button"
                className="pd-description-toggle"
                onClick={() => setShowFullDescription(prev => !prev)}
              >
                {showFullDescription ? 'Show less' : 'Learn more about this item'}
              </button>
            )}

            {/* Size Selector */}
            <div className="pd-section">
              <div className="pd-section-header">
                <span className="pd-section-label">{lang === 'de' ? t('product.selectSize').toUpperCase() : 'SELECT SIZE'}</span>
              </div>
              <select
                className="pd-size-select"
                value={selectedSizeObj?.size || ''}
                onChange={(e) => setSelectedSizeObj(parsedSizes.find(s => s.size === e.target.value))}
                disabled={parsedSizes.length === 0}
              >
                {parsedSizes.length > 0 ? parsedSizes.map((s, i) => (
                  <option key={i} value={s.size}>
                    {s.size}
                  </option>
                )) : (
                  <option value="">{lang === 'de' ? 'Standardgröße' : 'Standard Size'}</option>
                )}
              </select>
            </div>

            {/* Material Selector */}
            {parsedMaterials.length > 0 && (
              <div className="pd-section">
                <div className="pd-section-header">
                  <span className="pd-section-label">{lang === 'de' ? 'MATERIAL WÄHLEN' : 'SELECT MATERIAL'}</span>
                </div>
                <select
                  className="pd-size-select"
                  value={selectedMaterial || ''}
                  onChange={(e) => setSelectedMaterial(e.target.value)}
                >
                  {parsedMaterials.map((m, i) => (
                    <option key={i} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Quantity + Add to Cart */}
            <div className="pd-actions">
              <div className="pd-qty">
                <button className="pd-qty-btn" onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
                <span className="pd-qty-num">{quantity}</span>
                <button className="pd-qty-btn" onClick={() => setQuantity(Math.min(maxSelectableQty, quantity + 1))}>+</button>
              </div>
              <button
                className={`pd-add-cart ${added ? 'added' : ''}`}
                onClick={handleAddToCart}
                disabled={outOfStock}
              >
                {outOfStock ? (lang === 'de' ? 'NICHT AUF LAGER' : 'OUT OF STOCK') : (added ? (lang === 'de' ? '✓ HINZUGEFÜGT' : '✓ ADDED TO CART') : (lang === 'de' ? t('product.addToCart').toUpperCase() : 'ADD TO CART'))}
              </button>
            </div>

            {/* Wishlist */}
            <div style={{ marginBottom: '30px' }}>
              <button className="pd-wishlist" onClick={() => setWishlisted(!wishlisted)}>
                <span>{wishlisted ? '♥' : '♡'}</span> {wishlisted ? (lang === 'de' ? 'Auf der Wunschliste' : 'Added to Wishlist') : (lang === 'de' ? 'Zur Wunschliste hinzufügen' : 'Add to Wishlist')}
              </button>
            </div>

          </div>{/* end pd-info */}
        </div>{/* end pd-layout */}

        {/* ─── Full-width section below image + info ─── */}
        <div className="pd-bottom-section">
          {/* ─── Accordion Sections ─── */}
          <div className="pd-accordions">
            {[
              {
                key: 'details', title: lang === 'de' ? 'Produktdetails' : 'Product Details', content: (
                  <ul className="pd-detail-list">
                    {(selectedMaterial || p.materials) && <li><strong>{lang === 'de' ? 'Material' : 'Material'}:</strong> {(selectedMaterial || p.materials).replace(/&amp;/g, '&')}</li>}
                    {p.constructions && <li><strong>{lang === 'de' ? 'Technik' : 'Technique'}:</strong> {p.constructions}</li>}
                    <li><strong>{lang === 'de' ? 'Herkunft' : 'Origin'}:</strong> Bhadohi - Carpet City</li>
                    {p.colors && <li><strong>{lang === 'de' ? 'Farben' : 'Colors'}:</strong> {p.colors}</li>}
                    {p.shapes && <li><strong>{lang === 'de' ? 'Form' : 'Shape'}:</strong> {p.shapes}</li>}
                    {p.pile_height && <li><strong>{lang === 'de' ? 'Florhöhe / Stärke' : 'Pile Height / Thickness'}:</strong> {p.pile_height}</li>}
                    {p.rooms && <li><strong>{lang === 'de' ? 'Empfohlener Raum' : 'Recommended Room'}:</strong> {p.rooms}</li>}
                    {p.category_name && <li><strong>{lang === 'de' ? 'Kollektion' : 'Collection'}:</strong> {translatedCategory || p.category_name}</li>}

                    {!p.materials && !p.constructions && !p.colors && !p.shapes && !p.rooms && !p.category_name && (
                      <li><em>{lang === 'de' ? 'Noch keine spezifischen Details hinzugefügt.' : 'No specific details added yet.'}</em></li>
                    )}
                  </ul>
                )
              },
              {
                key: 'shipping', title: lang === 'de' ? 'Versand & Rückgabe' : 'Shipping & Returns', content: (
                  <div className="pd-accordion-text">
                    <p>{lang === 'de' ? 'Erfüllung' : 'Fulfillment'}: <strong>{deliveryTypeLabel}</strong>. {lang === 'de' ? 'Voraussichtliches Versandfenster' : 'Estimated dispatch window'}: <strong>{dispatchWindowText}</strong>.</p>
                    <p>{lang === 'de' ? 'Kostenloser Versand für alle Bestellungen über ₹5.000. Die endgültige Transitzeit kann je nach Lieferort variieren.' : 'Free shipping on all orders above ₹5,000. Final transit timeline may vary by delivery location.'}</p>
                    <p>{lang === 'de' ? '30 Tage problemlose Rückgabe. Wir arrangieren die Abholung vor Ihrer Haustür.' : '30-day hassle-free returns. We\'ll arrange pickup from your doorstep.'}</p>
                  </div>
                )
              },
              {
                key: 'care', title: lang === 'de' ? 'Pflegehinweise' : 'Care Instructions', content: (
                  <div className="pd-accordion-text">
                    <p>{lang === 'de' ? 'Professionelle chemische Reinigung empfohlen. Regelmäßig mit geringer Saugkraft saugen. Alle 6 Monate für gleichmäßige Abnutzung drehen. Direkte Sonneneinstrahlung über längere Zeiträume vermeiden.' : 'Professional dry cleaning recommended. Vacuum regularly on low suction. Rotate every 6 months for even wear. Avoid direct sunlight for prolonged periods.'}</p>
                  </div>
                )
              },
            ].map(section => (
              <div key={section.key} className={`pd-accordion ${activeAccordion === section.key ? 'open' : ''}`}>
                <button className="pd-accordion-header" onClick={() => toggleAccordion(section.key)}>
                  <span>{section.title}</span>
                  <span className="pd-accordion-icon">{activeAccordion === section.key ? '−' : '+'}</span>
                </button>
                {activeAccordion === section.key && (
                  <div className="pd-accordion-body">{section.content}</div>
                )}
              </div>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="pd-trust">
            {[
              { icon: freeDeliveryIcon, label: lang === 'de' ? 'Kostenloser Versand' : 'Free Shipping' },
              { icon: handcraftedIcon, label: lang === 'de' ? 'Handgefertigt' : 'Handcrafted' },
              { icon: returnsIcon, label: lang === 'de' ? '30 Tage Rückgabe' : '30-Day Returns' },
              { icon: checkoutIcon, label: lang === 'de' ? 'Sicherer Checkout' : 'Secure Checkout' },
            ].map((badge, i) => (
              <div key={i} className="pd-trust-item">
                <img src={badge.icon} alt={badge.label} className="pd-trust-icon-img" />
                <span className="pd-trust-label">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ REVIEW SECTION ═══ */}
        <div className="pd-reviews-section">
          <h2 className="pd-reviews-title">{lang === 'de' ? 'Bewertungen für diesen Artikel' : 'Reviews for this item'}</h2>

          {/* Summary Bar */}
          <div className="pd-review-summary">
            <div className="pd-review-avg">
              <span className="pd-review-avg-num">{reviewAvg}</span>
              <div>
                <div className="pd-stars">
                  {[1,2,3,4,5].map(s => <span key={s} className={`pd-star-icon ${s <= Math.round(reviewAvg) ? 'filled' : ''}`}>★</span>)}
                </div>
                <span className="pd-review-count">({reviewTotal} {lang === 'de' ? (reviewTotal !== 1 ? 'Bewertungen' : 'Bewertung') : (reviewTotal !== 1 ? 'reviews' : 'review')})</span>
              </div>
            </div>
          </div>

          {/* Review List */}
          <div className="pd-review-list">
            {reviews.length > 0 ? reviews.map(r => {
              let imgs = [];
              try { imgs = JSON.parse(r.review_images || '[]'); } catch(e) {}
              return (
                <div key={r.id} className="pd-review-item">
                  <div className="pd-review-top">
                    <div className="pd-review-stars">
                      {[1,2,3,4,5].map(s => <span key={s} className={`pd-star-icon ${s <= r.rating ? 'filled' : ''}`}>★</span>)}
                      <span style={{ fontWeight: '600', marginLeft: '6px' }}>{r.rating}</span>
                      <span className="pd-verified-badge">✓ Verified Purchase</span>
                    </div>
                    <div className="pd-review-meta">
                      <span className="pd-review-author">{r.user_name}</span>
                      <span className="pd-review-date">{new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                  </div>
                  <p className="pd-review-text">{r.review_text}</p>
                  {imgs.length > 0 && (
                    <div className="pd-review-images">
                      {imgs.map((img, i) => <img key={i} src={img} alt="Review" className="pd-review-img" />)}
                    </div>
                  )}
                </div>
              );
            }) : (
              <p style={{ color: '#999', padding: '20px 0' }}>No reviews yet. Be the first to review!</p>
            )}
          </div>

          {/* Write Review Form */}
          {user && canReview && (
            <div className="pd-write-review">
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>{lang === 'de' ? t('product.writeReview') : 'Write a Review'}</h3>
              <div className="pd-star-picker">
                {[1,2,3,4,5].map(s => (
                  <span key={s} className={`pd-star-pick ${s <= reviewForm.rating ? 'active' : ''}`} onClick={() => setReviewForm({ ...reviewForm, rating: s })}>★</span>
                ))}
              </div>
              <textarea
                className="pd-review-textarea"
                placeholder={lang === 'de' ? 'Teilen Sie Ihre Erfahrungen mit diesem Produkt...' : "Share your experience with this product..."}
                value={reviewForm.text}
                onChange={e => setReviewForm({ ...reviewForm, text: e.target.value })}
              />
              <div className="pd-review-upload-row">
                <label className="pd-review-upload-btn">
                  📷 {lang === 'de' ? 'Fotos hinzufügen' : 'Add Photos'}
                  <input type="file" accept="image/*" multiple hidden onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    for (const file of files) {
                      const fd = new FormData();
                      fd.append('image', file);
                      try {
                        const res = await fetch(API.reviews.uploadImage, { method: 'POST', headers: { 'Authorization': `Bearer ${user.token}` }, body: fd });
                        const d = await res.json();
                        if (d.status === 'success') setReviewForm(prev => ({ ...prev, images: [...prev.images, d.url] }));
                      } catch(err) {}
                    }
                  }} />
                </label>
                {reviewForm.images.length > 0 && (
                  <div className="pd-review-preview-imgs">
                    {reviewForm.images.map((img, i) => <img key={i} src={img} alt="" style={{ width: '50px', height: '50px', borderRadius: '6px', objectFit: 'cover' }} />)}
                  </div>
                )}
              </div>
              <button
                className="pd-submit-review"
                disabled={submittingReview}
                onClick={async () => {
                  setSubmittingReview(true);
                  try {
                    const res = await fetch(API.reviews.submit, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                      body: JSON.stringify({ product_id: product.id, rating: reviewForm.rating, review_text: reviewForm.text, review_images: JSON.stringify(reviewForm.images) })
                    });
                    const d = await res.json();
                    if (d.status === 'success') {
                      setReviewForm({ rating: 5, text: '', images: [] });
                      setCanReview(false);
                      // Refresh reviews
                      const rr = await fetch(API.reviews.list(product.id));
                      const rd = await rr.json();
                      if (rd.status === 'success') { setReviews(rd.data); setReviewAvg(rd.average); setReviewTotal(rd.total); }
                    } else { alert(d.message); }
                  } catch(err) { alert(lang === 'de' ? 'Bewertung konnte nicht gesendet werden.' : 'Failed to submit review.'); }
                  setSubmittingReview(false);
                }}
              >{submittingReview ? (lang === 'de' ? 'Wird gesendet...' : 'Submitting...') : (lang === 'de' ? t('product.submitReview') : 'Submit Review')}</button>
            </div>
          )}
          {user && !canReview && reviews.length === 0 && (
            <p style={{ fontSize: '13px', color: '#999', marginTop: '10px' }}>{lang === 'de' ? 'Kaufen und erhalten Sie dieses Produkt, um eine Bewertung zu hinterlassen.' : 'Purchase and receive this product to leave a review.'}</p>
          )}
        </div>

        {/* ═══ RELATED PRODUCTS ═══ */}
        {relatedProducts.length > 0 && (
          <div className="pd-related-section">
            <h2 className="pd-related-title">{lang === 'de' ? t('product.relatedProducts') : 'You May Also Like'}</h2>
            <div className="pd-related-grid">
              {relatedProducts.map(rp => (
                <RelatedProductCard key={rp.id} product={rp} />
              ))}
            </div>
          </div>
        )}

        <style>{pdStyles}</style>
      </div>
    </div>
  );
}

function RelatedProductCard({ product }) {
  const { lang, t } = useLanguage();
  const translatedName = useDynamicTranslation(product.name);
  const translatedCategory = useDynamicTranslation(product.category_name);
  return (
    <Link to={`/product/${product.sku || product.id}`} className="pd-related-card">
      <div className="pd-related-img" style={{ backgroundImage: `url("${product.image_url}")` }} />
      <div className="pd-related-info">
        <p className="pd-related-name">{translatedName || product.name}</p>
        <p className="pd-related-cat">{translatedCategory || product.category_name || (lang === 'de' ? t('common.handcrafted') : 'Handcrafted')}</p>
      </div>
    </Link>
  );
}

/* ═══════════════════════════════════════════
   PRODUCT DETAIL STYLES
   ═══════════════════════════════════════════ */
const pdStyles = `
/* Page */
.pd-wrapper {
  background: #fbfaee;
  min-height: 100vh;
  width: 100%;
}
.pd-page {
  padding: 0 60px 60px;
  max-width: 1400px;
  margin: 0 auto;
}
.pd-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #888;
  background: #fbfaee;
}
.pd-loader {
  width: 32px; height: 32px;
  border: 3px solid #eee;
  border-top-color: #785600;
  border-radius: 50%;
  animation: pdSpin 0.8s linear infinite;
  margin-bottom: 16px;
}
@keyframes pdSpin { to { transform: rotate(360deg); } }

/* Breadcrumb */
.pd-breadcrumb {
  padding: 24px 0;
  font-size: 12px;
  color: #999;
  letter-spacing: 0.5px;
}
.pd-breadcrumb a { color: #888; text-decoration: none; }
.pd-breadcrumb a:hover { color: #1b1c15; text-decoration: underline; }
.pd-breadcrumb span { margin: 0 6px; }
.pd-breadcrumb .bc-current { color: #1b1c15; }

/* Layout */
.pd-layout {
  display: flex;
  gap: 60px;
}

/* Bottom section — full width below columns */
.pd-bottom-section {
  margin-top: 50px;
  max-width: 900px;
}

/* ═══ Gallery ═══ */
.pd-gallery { 
  flex: 1.25; 
  display: flex;
  gap: 24px;
}

.pd-thumbs {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 80px;
  flex-shrink: 0;
}
.pd-thumb {
  width: 100%;
  aspect-ratio: 1/1;
  background: #efeee3;
  background-size: cover;
  background-position: center;
  border-radius: 6px;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.2s, outline-color 0.2s;
  outline: 2px solid transparent;
  outline-offset: 2px;
}
.pd-thumb.active, .pd-thumb:hover {
  opacity: 1;
  outline-color: #785600;
}

.pd-hero-img {
  flex: 1;
  position: relative;
  height: 65vh;
  min-height: 400px;
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pd-hero-inner {
  position: absolute;
  inset: 0 60px; /* Provides 60px margin left/right so arrows don't cover the rug */
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  transition: transform 0.5s ease;
  cursor: zoom-in;
}
.pd-hero-img:hover .pd-hero-inner { transform: scale(1.08); }

.pd-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #eee;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #1b1c15;
  transition: all 0.2s;
  z-index: 2;
}
.pd-nav-btn:hover {
  background: #f5f4e8;
  border-color: #d3c4af;
}
.pd-nav-btn.prev { left: 8px; }
.pd-nav-btn.next { right: 8px; }

.pd-zoom-btn {
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: rgba(255,255,255,0.9);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 18px;
  cursor: pointer;
  color: #785600;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 2;
}
.pd-hero-img:hover .pd-zoom-btn { opacity: 1; }

/* ═══ Info ═══ */
.pd-info { flex: 1; padding-top: 10px; }

.pd-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2px;
  color: #785600;
  display: block;
  margin-bottom: 12px;
}

.pd-title {
  font-family: 'Noto Serif', Georgia, serif;
  font-size: 2.4rem;
  font-weight: 400;
  color: #1b1c15;
  margin: 0 0 14px;
  line-height: 1.15;
  letter-spacing: -0.5px;
}

.pd-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
}
.pd-rating { display: flex; align-items: center; gap: 2px; }
.pd-star { color: #f7bd48; font-size: 14px; }
.pd-rating-text { font-size: 12px; color: #888; margin-left: 8px; }
.pd-sku { font-size: 12px; color: #aaa; letter-spacing: 0.5px; }
.pd-delivery-pill {
  font-size: 11px;
  color: #3f3d37;
  background: #ece6d8;
  border: 1px solid #dfd6c1;
  border-radius: 999px;
  padding: 5px 10px;
  letter-spacing: 0.3px;
}

.pd-price-row {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 22px;
}
.pd-price {
  font-size: 2rem;
  font-weight: 500;
  color: #1b1c15;
}
.pd-mrp {
  font-size: 15px;
  color: #bbb;
  text-decoration: line-through;
}
.pd-trade-badge {
  font-size: 10px;
  font-weight: 600;
  background: #785600;
  color: #fff;
  padding: 3px 10px;
  border-radius: 3px;
  letter-spacing: 1px;
}
.pd-b2b-hint {
  font-size: 12px;
  color: #999;
  background: #f5f4e8;
  padding: 3px 10px;
  border-radius: 3px;
}
.pd-discount-badge {
  font-size: 10px;
  font-weight: 700;
  color: #b42318;
  background: #ffe9e7;
  border: 1px solid #f7c8c2;
  border-radius: 999px;
  padding: 3px 9px;
  letter-spacing: 0.8px;
}
.pd-stock-note {
  font-size: 12px;
  margin-top: -10px;
  margin-bottom: 18px;
  letter-spacing: 0.2px;
}
.pd-stock-note.low { color: #9a3412; }
.pd-stock-note.out { color: #b91c1c; font-weight: 600; }

.pd-description {
  position: relative;
  font-size: 14.5px;
  color: #4f4535;
  line-height: 1.8;
  margin-bottom: 10px;
}
.pd-description.collapsed {
  max-height: 130px;
  overflow: hidden;
}
.pd-description-fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 58px;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(243, 243, 236, 0), #f3f3ec 80%);
}
.pd-description-toggle {
  border: none;
  background: transparent;
  padding: 0;
  margin-bottom: 28px;
  font-size: 14px;
  font-weight: 600;
  color: #2f2f2f;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 4px;
}
.pd-description-toggle:hover {
  color: #785600;
}
.pd-description h1, .pd-description h2, .pd-description h3 {
  color: #1b1c15;
  margin-top: 15px;
  margin-bottom: 10px;
  font-weight: 500;
}
.pd-description ul {
  padding-left: 20px;
  margin-bottom: 15px;
  list-style-type: disc;
}
.pd-description ol {
  padding-left: 20px;
  margin-bottom: 15px;
}
.pd-description li {
  margin-bottom: 5px;
}
.pd-description p {
  margin-bottom: 15px;
}

/* Size */
.pd-section { margin-bottom: 28px; }
.pd-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.pd-section-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  color: #1b1c15;
}
.pd-size-guide {
  font-size: 12px;
  color: #785600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.pd-size-select {
  width: 100%;
  padding: 14px 18px;
  border: 1.5px solid #d3c4af;
  border-radius: 6px;
  font-size: 15px;
  font-family: inherit;
  color: #1b1c15;
  background: transparent;
  cursor: pointer;
  appearance: auto;
  outline: none;
  transition: all 0.2s;
}
.pd-size-select:focus {
  border-color: #785600;
}

/* Actions */
.pd-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}
.pd-qty {
  display: flex;
  align-items: center;
  border: 1.5px solid #d3c4af;
  border-radius: 4px;
  overflow: hidden;
}
.pd-qty-btn {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  color: #1b1c15;
  transition: background 0.2s;
}
.pd-qty-btn:hover { background: #f5f4e8; }
.pd-qty-num {
  width: 36px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
}

.pd-add-cart {
  flex: 1;
  padding: 20px 30px;
  min-height: 64px;
  background: linear-gradient(135deg, #785600, #986d00);
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pd-add-cart:hover { opacity: 0.92; transform: translateY(-1px); }
.pd-add-cart.added {
  background: linear-gradient(135deg, #2d6a30, #3a8a3e);
}
.pd-add-cart:disabled {
  background: #bdb7aa;
  cursor: not-allowed;
  transform: none;
  opacity: 0.8;
}

/* Wishlist */
.pd-wishlist {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  font-size: 14px;
  color: #785600;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 32px;
  transition: color 0.2s;
}
.pd-wishlist:hover { color: #986d00; }
.pd-wishlist span { font-size: 18px; }

/* Accordions */
.pd-accordions { margin-bottom: 32px; }
.pd-accordion {
  border-top: 1px solid rgba(211,196,175,0.4);
}
.pd-accordion:last-child { border-bottom: 1px solid rgba(211,196,175,0.4); }
.pd-accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px 0;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #1b1c15;
  cursor: pointer;
}
.pd-accordion-icon { font-size: 18px; color: #785600; }
.pd-accordion-body {
  padding: 0 0 18px;
  animation: pdSlideDown 0.25s ease;
}
@keyframes pdSlideDown { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }

.pd-detail-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.pd-detail-list li {
  font-size: 13.5px;
  color: #4f4535;
  padding: 5px 0;
  line-height: 1.6;
}
.pd-detail-list li strong {
  color: #1b1c15;
  font-weight: 500;
  margin-right: 6px;
}
.pd-accordion-text p {
  font-size: 13.5px;
  color: #4f4535;
  line-height: 1.7;
  margin: 0 0 8px;
}

/* Trust Badges */
.pd-trust {
  display: flex;
  justify-content: space-between;
  padding: 24px 20px;
  background: #f5f4e8;
  border-radius: 6px;
}
.pd-trust-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.pd-trust-icon-img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}
.pd-trust-label {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.8px;
  color: #4f4535;
  text-transform: uppercase;
}

/* Share Button — in breadcrumb bar */
.pd-share-btn {
  position: absolute;
  right: 0;
  top: 75%;
  width: 38px;
  height: 38px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #555;
  transition: all 0.2s ease;
}
.pd-share-btn:hover {
  color: #785600;
  border-color: #785600;
}

/* ═══ REVIEW SECTION ═══ */
.pd-reviews-section {
  margin-top: 50px;
  padding-top: 40px;
  border-top: 1px solid #e0ddd4;
}
.pd-reviews-title {
  font-family: var(--font-serif, Georgia, serif);
  font-size: 1.5rem;
  font-weight: 400;
  color: #1b1c15;
  margin-bottom: 24px;
}
.pd-review-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  padding: 20px 24px;
  background: #f5f4e8;
  border-radius: 8px;
}
.pd-review-avg {
  display: flex;
  align-items: center;
  gap: 12px;
}
.pd-review-avg-num {
  font-size: 2.4rem;
  font-weight: 700;
  color: #1b1c15;
}
.pd-stars { display: flex; gap: 2px; }
.pd-star-icon { font-size: 18px; color: #d3c4af; }
.pd-star-icon.filled { color: #f5a623; }
.pd-review-count {
  font-size: 13px;
  color: #888;
  margin-top: 2px;
}
.pd-review-list { }
.pd-review-item {
  padding: 24px 0;
  border-bottom: 1px solid #eee;
}
.pd-review-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}
.pd-review-stars { display: flex; align-items: center; gap: 2px; }
.pd-verified-badge {
  margin-left: 10px;
  font-size: 11px;
  font-weight: 600;
  color: #2d6a30;
  background: #e6f4ea;
  padding: 2px 8px;
  border-radius: 4px;
  letter-spacing: 0.3px;
}
.pd-review-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #888;
}
.pd-review-author { font-weight: 600; color: #555; }
.pd-review-date { color: #aaa; }
.pd-review-text {
  font-size: 14.5px;
  line-height: 1.7;
  color: #333;
}
.pd-review-images {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.pd-review-img {
  width: 70px;
  height: 70px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  border: 1px solid #eee;
}
.pd-write-review {
  margin-top: 32px;
  padding: 24px;
  background: #faf9f2;
  border-radius: 8px;
  border: 1px solid #e8e5d8;
}
.pd-star-picker {
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
}
.pd-star-pick {
  font-size: 28px;
  color: #d3c4af;
  cursor: pointer;
  transition: color 0.15s;
}
.pd-star-pick.active { color: #f5a623; }
.pd-star-pick:hover { color: #f5a623; }
.pd-review-textarea {
  width: 100%;
  min-height: 100px;
  padding: 14px;
  border: 1.5px solid #d3c4af;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  color: #1b1c15;
  background: transparent;
  resize: vertical;
  outline: none;
  margin-bottom: 12px;
}
.pd-review-textarea:focus { border-color: #785600; }
.pd-review-upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.pd-review-upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: 1.5px dashed #785600;
  border-radius: 6px;
  color: #785600;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}
.pd-review-upload-btn:hover { background: rgba(120,86,0,0.05); }
.pd-review-preview-imgs { display: flex; gap: 6px; }
.pd-submit-review {
  padding: 14px 32px;
  background: linear-gradient(135deg, #785600, #986d00);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  transition: opacity 0.2s;
}
.pd-submit-review:hover { opacity: 0.9; }
.pd-submit-review:disabled { opacity: 0.6; cursor: not-allowed; }

/* ═══ RELATED PRODUCTS ═══ */
.pd-related-section {
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #e0ddd4;
}
.pd-related-title {
  font-family: var(--font-serif, Georgia, serif);
  font-size: 1.5rem;
  font-weight: 400;
  color: #1b1c15;
  margin-bottom: 28px;
}
.pd-related-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}
.pd-related-card {
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s;
}
.pd-related-card:hover { transform: translateY(-4px); }
.pd-related-img {
  width: 100%;
  aspect-ratio: 4/5;
  background-size: cover;
  background-position: center;
  background-color: #f0ede8;
  border-radius: 6px;
  margin-bottom: 10px;
}
.pd-related-info { padding: 0 4px; }
.pd-related-name {
  font-size: 13.5px;
  font-weight: 500;
  color: #1b1c15;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pd-related-cat {
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

/* ═══ Responsive ═══ */
@media (max-width: 900px) {
  .pd-page { padding: 0 20px 40px; }
  .pd-layout { flex-direction: column; gap: 30px; }
  .pd-gallery { flex-direction: column-reverse; gap: 16px; }
  .pd-thumbs { flex-direction: row; width: 100%; overflow-x: auto; gap: 8px; }
  .pd-thumb { width: 64px; height: 64px; flex-shrink: 0; }
  .pd-hero-img { height: 50vh; min-height: 350px; }
  .pd-hero-inner { inset: 0 44px; }
  .pd-title { font-size: 1.8rem; }
  .pd-price { font-size: 1.6rem; }
  .pd-trust { flex-wrap: wrap; gap: 16px; justify-content: center; }
  .pd-related-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
}
@media (max-width: 480px) {
  .pd-page { padding: 0 14px 30px; }
  .pd-title { font-size: 1.5rem; }
  .pd-sizes { flex-wrap: wrap; }
  .pd-actions { flex-direction: column; gap: 12px; }
  .pd-qty { width: 100%; justify-content: space-between; height: 50px; border-color: #d3c4af; }
  .pd-qty-btn { width: 60px; height: 50px; font-size: 22px; }
  .pd-qty-num { font-size: 16px; }
  .pd-add-cart { width: 100%; min-height: 65px; font-size: 16px; padding: 20px 0; }
  .pd-hero-img { height: 45vh; min-height: 300px; }
  .pd-hero-inner { inset: 0 36px; }
  .pd-nav-btn { width: 32px; height: 32px; font-size: 16px; }
  .pd-nav-btn.prev { left: 4px; }
  .pd-nav-btn.next { right: 4px; }
  .pd-related-grid { grid-template-columns: repeat(2, 1fr); }
  .pd-review-top { flex-direction: column; }
}
`;
